import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { LOGIN } from "@/constants/testIds/auth";

// Página de login do AdminLTE (pages/examples/login.html), só com e-mail e senha.
// Centraliza num contêiner próprio (.login-page) em vez de depender de classes no <body>
export const LoginShell = ({ children }) => {
  const { skin } = useTheme();
  return (
    <div className="login-page" style={{ height: "auto", minHeight: "100vh" }}>
      <div className="login-box">
        {/* Só a logo enviada em Customização, sem o nome. Fundo escuro como no menu lateral,
            para logos claras/transparentes não sumirem no fundo cinza */}
        <div className="login-logo">
          {skin.brandLogo ? (
            <img src={skin.brandLogo} alt={skin.brandName} data-testid="login-logo"
              className={`${skin.brandLogoCircle ? "img-circle" : "rounded"} elevation-3 bg-dark`} style={{ width: 96, height: 96, objectFit: "contain", padding: 12 }} />
          ) : (
            <span data-testid="login-logo" className={`${skin.brandLogoCircle ? "img-circle" : "rounded"} elevation-3 bg-primary d-inline-flex align-items-center justify-content-center`}
              style={{ width: 96, height: 96, fontSize: 44 }} title={skin.brandName}>
              <b>{(skin.brandName || "?").trim().charAt(0).toUpperCase()}</b>
            </span>
          )}
        </div>
        {children}
      </div>
    </div>
  );
};

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState(sp.get("email") || "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password, remember);
      nav("/");
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <LoginShell>
      <div className="card">
        <div className="card-body login-card-body">
          <p className="login-box-msg">Entre para iniciar sua sessão</p>
          {error && <div className="alert alert-danger py-2" role="alert" data-testid="login-error">{error}</div>}
          <form onSubmit={submit} data-testid="login-form">
            <div className="input-group mb-3">
              <input data-testid={LOGIN.emailInput} type="email" className="form-control" placeholder="E-mail" autoComplete="username" required autoFocus={!email}
                value={email} onChange={(e) => setEmail(e.target.value)} />
              <div className="input-group-append"><div className="input-group-text"><span className="fas fa-envelope" /></div></div>
            </div>
            <div className="input-group mb-3">
              <input data-testid={LOGIN.passwordInput} type="password" className="form-control" placeholder="Senha" autoComplete="current-password" required autoFocus={!!email}
                value={password} onChange={(e) => setPassword(e.target.value)} />
              <div className="input-group-append"><div className="input-group-text"><span className="fas fa-lock" /></div></div>
            </div>
            <div className="row">
              <div className="col-7">
                <div className="icheck-primary custom-control custom-checkbox">
                  <input type="checkbox" className="custom-control-input" id="remember" data-testid="login-remember" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                  <label className="custom-control-label font-weight-normal" htmlFor="remember">Lembrar de mim</label>
                </div>
              </div>
              <div className="col-5">
                <button data-testid={LOGIN.submitButton} type="submit" className="btn btn-primary btn-block" disabled={loading}>
                  {loading && <i className="fas fa-spinner fa-spin mr-1" />}Entrar
                </button>
              </div>
            </div>
          </form>
          <p className="mb-1 mt-3">
            <Link to={`/esqueci-senha${email.trim() ? `?email=${encodeURIComponent(email.trim())}` : ""}`} data-testid={LOGIN.forgotPasswordLink}>
              Esqueci minha senha
            </Link>
          </p>
          <p className="mb-0 text-sm text-muted">Primeiro acesso? Use "Esqueci minha senha" com o e-mail cadastrado pelo gestor.</p>
        </div>
      </div>
    </LoginShell>
  );
}
